"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { FeedData as Feed } from "@extractus/feed-extractor"
import { ProcessedEntry } from "@/types/import"
import { SettingsHeader } from "../../../components/SettingsHeader"
import { ImportURLInput } from "./ImportPosts/ImportURLInput"
import { MirrorImport } from "./ImportPosts/MirrorImport"
import { RSSImport } from "./ImportPosts/RSSImport"
import { getMirrorPosts } from "@/lib/api/import"
import { ImportType, MirrorData } from "../types"

export default function ImportPosts({
  mirrorData,
}: {
  mirrorData: MirrorData
}) {
  const [feed, setFeed] = useState<Feed | null>(null)
  const [importType, setImportType] = useState<ImportType>(null)
  const [url, setUrl] = useState("")
  const [mirrorEntries, setMirrorEntries] = useState<Array<ProcessedEntry>>([])

  // Use the props instead of the hook
  const { wallet: usersWallet, hasMirrorAcc: mirrorAutoPrompt } = mirrorData
  const [isAutoMirrorLoading, setIsAutoMirrorLoading] = useState(false)

  const handleAutoMirrorImport = async () => {
    if (!usersWallet) return

    setIsAutoMirrorLoading(true)

    const feed = await getMirrorPosts(usersWallet)
    if (feed && feed.length > 0) {
      setMirrorEntries(feed)
      setImportType("MIRROR")
    }

    setIsAutoMirrorLoading(false)
  }

  function ImportHeader() {
    return (
      <SettingsHeader
        title="Import posts"
        description={
          <>
            Bring your writing from other platforms into Paragraph. Supported
            platforms include WordPress, Substack, and Mirror.{" "}
            <a
              href="https://docs.paragraph.com/docs/migrating-to-paragraph"
              className="text-primary underline"
            >
              Learn more
            </a>
          </>
        }
      />
    )
  }

  // Render different views based on the import type
  if (importType === "MIRROR") {
    return (
      <section className="space-y-6">
        <ImportHeader />
        <MirrorImport
          defaultEntries={mirrorEntries}
          onBack={() => setImportType(null)}
        />
        <Separator className="my-6" />
      </section>
    )
  } else if (importType === "RSS" && feed) {
    return (
      <section className="space-y-6">
        <ImportHeader />
        <RSSImport
          feed={feed}
          url={url}
          onBack={() => {
            setImportType(null)
            setFeed(null)
          }}
        />
        <Separator className="my-6" />
      </section>
    )
  }

  return (
    <section className="space-y-6">
      <ImportHeader />

      <ImportURLInput
        url={url}
        setUrl={setUrl}
        setFeed={setFeed}
        setImportType={setImportType}
      />

      {mirrorAutoPrompt && (
        <div className="flex flex-col space-y-4 p-4 rounded-md border border-border bg-muted/50 md:mx-0 mx-auto">
          <p className="text-sm">
            Your wallet address has published on Mirror. Import these posts into
            Paragraph?
          </p>
          <Button
            variant="default"
            className="max-w-[200px] md:mx-0 mx-auto"
            disabled={isAutoMirrorLoading}
            onClick={handleAutoMirrorImport}
          >
            {isAutoMirrorLoading ? "Checking Mirror..." : "Import from Mirror"}
          </Button>
        </div>
      )}
    </section>
  )
}
